<template>
  <div>
    <TitleWrapper
      :filter-option="false"
      :title="viewTitle"
      :tooltip-title="viewTitle"
      :display-breadcrumb="true"
    />
    <CalendarActionsWrapper
      class="mt-2"
      :calendar-date="dateRange.startDate"
      :is-concise-view="true"
      :filters="[month]"
      @selectDate="setDate"
      @applyFilter="applyFilter"
    />
    <div class="mt-2">
      <UiCalendar
        :key="forceReRender"
        :calendar-date="dateRange.startDate"
        class="bg-primary-white"
        :events="birthdays"
      >
        <template v-slot="{ events: slotEvents }">
          <div
            v-for="(event, index) in slotEvents.slice(0, 3)"
            :key="index"
            class="flex items-center justify-center mb-0.5"
          >
            <button
              type="button"
              class="text-xs truncate w-23 inline-block text-center px-1 rounded-sm bg-primary-purple-100 text-primary-purple-600 hover:bg-primary-purple-200"
              @click="toggleBirthdayModal(event)"
            >
              {{ event.studentName }}
            </button>
          </div>
          <div v-if="slotEvents.length > 3" class="flex justify-center mt-1 mb-2">
            <p
              class="font-roboto font-medium text-primary-grey-light cursor-pointer text-xs"
              @click="toggleBirthdayModal(slotEvents)"
            >
              View more ...
            </p>
          </div>
        </template>
      </UiCalendar>
    </div>

    <BirthdayModal
      :modal-show="showModal"
      :users="birthdayStudentsDetails"
      @close="toggleBirthdayModal('close')"
    />
  </div>
</template>

<script>
import BirthdayModal from '@/src/components/Modals/BirthdayDetailModal.vue'
import CalendarActionsWrapper from '@components/Calendar/CalendarActionsWrapper.vue'
import TitleWrapper from '@components/TitleWrapper.vue'
import UiCalendar from '@components/Calendar/index.vue'
import { CALENDER_VIEWS } from '@src/constants/general-constants'
import { TENANT_ROLES } from '@src/constants/user-roles-constants.js'
import { mapActions, mapState } from 'vuex'
import GENERAL_CONSTANTS from '@src/constants/general-constants'
export default {
  name: 'StaffBirthdayView',
  components: { BirthdayModal, CalendarActionsWrapper, TitleWrapper, UiCalendar },
  data() {
    return {
      forceReRender: 1,
      dateRange: {
        startDate: new Date(),
      },
      birthdays: [],
      isLoading: false,
      showModal: false,
      birthdayStudentsDetails: [],
      month: CALENDER_VIEWS.MONTH,
      viewTitle: 'STUDENT BIRTHDAYS',
    }
  },
  computed: {
    ...mapState('layout', ['currentCampusScope', 'currentClassScope', 'currentSectionScope']),
    currentScopeSignature() {
      const campusId = this.currentCampusScope?.id || 'no-campus'
      const classId = this.currentClassScope?.id || 'no-class'
      const sectionId = this.currentSectionScope?.id || 'no-section'
      return `${campusId}-${classId}-${sectionId}`
    },
  },
  watch: {
    currentScopeSignature() {
      this.applyFilter()
    },
  },
  created() {
    this.applyFilter()
  },
  methods: {
    ...mapActions('birthday', ['fetchBirthdays']),
    ...mapActions('layout', ['setRightbarContent']),

    setDate(date) {
      this.dateRange.startDate = new Date(date)
    },

    async applyFilter() {
      try {
        this.isLoading = true
        this.forceReRender++
        const month = this.dateRange.startDate.getMonth()
        const year = this.dateRange.startDate.getFullYear()
        const startOfMonth = new Date(year, month, 1)
        const endOfMonth = new Date(year, month + 1, 0)

        const payload = {
          start_date: this.formatDate(startOfMonth),
          end_date: this.formatDate(endOfMonth),
          roles: [TENANT_ROLES.SECTION_STUDENT],
        }
        const res = await this.fetchBirthdays(payload)
        const records = res || []

        this.birthdays = this.buildBirthdayEventsForMonth(records, startOfMonth, endOfMonth)
        this.updateRightbarContent()
      } finally {
        this.isLoading = false
      }
    },

    buildBirthdayEventsForMonth(students, startOfMonth, endOfMonth) {
      const pad = (number) => (number < 10 ? `0${number}` : `${number}`)

      return students
        .map((student) => {
          const eventDate = this.getBirthdayDate(student, startOfMonth)
          if (!eventDate) return null
          const studentRecord = student.student || student || {}
          const roleId = this.extractRoleId(student) || this.extractRoleId(studentRecord)
          return {
            date: `${eventDate.getFullYear()}-${pad(eventDate.getMonth() + 1)}-${pad(
              eventDate.getDate(),
            )}`,
            startTime: '00:00:00',
            endTime: '23:59:59',
            studentName:
              student.studentName ||
              student.student_name ||
              `${studentRecord.first_name || ''} ${studentRecord.last_name || ''}`.trim(),
            student: studentRecord,
            role: roleId,
          }
        })
        .filter(Boolean)
        .filter((event) => {
          const date = new Date(event.date)
          return date >= startOfMonth && date <= endOfMonth
        })
        .sort((event1, event2) => new Date(event1.date) - new Date(event2.date))
    },

    getBirthdayDate(student, startOfMonth) {
      if (student.date) {
        const parsedDate = new Date(student.date)
        return isNaN(parsedDate) ? null : parsedDate
      }
      const studentRecord = student.student || student
      const dateOfBirth =
        student.date_of_birth ||
        studentRecord?.profile?.date_of_birth ||
        studentRecord?.date_of_birth
      if (!dateOfBirth) return null
      const currentYear = startOfMonth.getFullYear()
      const dob = new Date(dateOfBirth)
      if (isNaN(dob)) return null
      return new Date(currentYear, dob.getMonth(), dob.getDate())
    },

    formatDate(date) {
      const month = `${date.getMonth() + 1}`.padStart(2, '0')
      const day = `${date.getDate()}`.padStart(2, '0')
      return `${date.getFullYear()}-${month}-${day}`
    },

    setModalState(students, showModal) {
      this.birthdayStudentsDetails = students
      this.showModal = showModal
    },

    extractRoleId(record) {
      if (!record) return null
      if (Array.isArray(record.users_roles_entities) && record.users_roles_entities.length) {
        const [primaryRole] = record.users_roles_entities
        return primaryRole?.role_id || primaryRole?.role || null
      }
      return record.role_id || record.role || record.user_role || record.user_role_id || null
    },
    toggleBirthdayModal(payload) {
      if (payload === 'close' || payload === false) {
        this.setModalState([], false)
        return
      }
      const students = Array.isArray(payload)
        ? payload.map((event) => event.student)
        : [payload.student]
      this.setModalState(students, true)
    },

    updateRightbarContent() {
      const count = this.birthdays?.length || 0
      this.setRightbarContent({
        header: null,
        profileInfo: [],
        userInfo: [],
        chartData: null,
        stats: [
          {
            title: GENERAL_CONSTANTS.STATS_AND_SUMMARY,
            categories: [{ text: 'STUDENTS_WITH_BIRTHDAYS', value: count }],
          },
        ],
      })
    },
  },
}
</script>
